import {
  DashboardState,
  ECardinality,
  ENotificationTemplateType,
  ESdChangeReason,
  ESymbolCategory,
  EWFAssignmentType,
  EWFDueUnit,
  EWFParamType,
  ISymbol,
  IWFNodeAssignment,
  IWFParameter,
  IWFStep,
  IWorkFlow,
  LeftPanelType,
  RightPanelType
} from "@kuika/kuika-cl-model";
import { IProjectsWithExternalWFResponseDto } from "@kuika/kuika-cl-model/src/dtos/projects-response";
import { IMailInteraction, ITemplateParameter, IUserInteraction } from "@kuika/kuika-cl-model/src/models/workflow";
import { ReactNode } from "react";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaCheckbox from "../../../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaDropdownMenu, {
  KuikaDropdownMenuItemType
} from "../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../../ui-kit/kuika-switch/kuika-switch";
import KuikaTabContent from "../../../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs from "../../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { requestFormsInteractions, requestFormsStep } from "../../../flows/process-editor/forms-interactions-focus-bus";
import { isInteractionNotifiable } from "../../../flows/process-editor/forms-tab-steps";
import { requestProcessEditorTab } from "../../../flows/process-editor/process-editor-tab-bus";
import { PROCESS_TAB_FORMS } from "../../../flows/process-editor/process-editor-tabs";
import { getWorkflowParamTypeLabel } from "../../../left-panel/work-flow-list/workflow-parameter-type-options";
import ActionParameterTitle from "../../properties-panel/action-parameter-title";
import { ActionPicker } from "../../properties-panel/action-picker";
import ActionSectionEmptyState from "../../properties-panel/action-section-empty-state";
import { SymbolPicker } from "../../properties-panel/symbol-picker";
import {
  getBpmnMiState,
  readMiCollectionSource,
  watchBpmnMi,
  writeMiCollectionSource
} from "../wf-utils/multi-instance-utils";
import { WorkflowAssignmentProperties } from "../wf-utils/workflow-assignment-properties";
import { WorkflowDue } from "../wf-utils/workflow-due";
import { WorkflowStepDescription } from "../wf-utils/workflow-step-description";
import { WorkflowStepOutputMapping } from "../wf-utils/workflow-step-output-mapping";
import "./workflow-screen-step-properties.scss";
import { AggregationProperties } from "./aggregation-properties";
import { LoopDecisionProperties } from "./loop-decision-properties";
import { PmiDecisionProperties } from "./pmi-decision-properties";
import { SmiDecisionProperties } from "./smi-decision-properties";

/** The step's Properties / Advanced tabs, as `KuikaTabs` holds them, in their on-screen order. */
const PROPERTIES_STEP_TAB: string = "Properties";
const ADVANCED_STEP_TAB: string = "Advanced";
const STEP_TAB_ORDER: string[] = [PROPERTIES_STEP_TAB, ADVANCED_STEP_TAB];

/** The notification mail's Template tabs, as `KuikaTabs` holds them, in their on-screen order. */
const DEFAULT_MAIL_TEMPLATE: string = "Default";
const CUSTOM_MAIL_TEMPLATE: string = "Custom";
const MAIL_TEMPLATE_ORDER: string[] = [DEFAULT_MAIL_TEMPLATE, CUSTOM_MAIL_TEMPLATE];

export interface IWorkflowScreenStepPropertiesState {
  step: IWFStep;
  screen: string | undefined;
  inputPanel: boolean;
  loading: boolean;
  mailInteractionModalVisible: boolean;
  userInteractionModalVisible: boolean;
  projectList: IProjectsWithExternalWFResponseDto[];
  visibleAppsDropdown: boolean;
  searchText: string;
  filteredProjectList: IProjectsWithExternalWFResponseDto[];
  visibleMailInteractionDropdown: boolean;
  advancedPropertiesVisible: boolean;
  miStamp?: number;
}

export interface IWorkflowScreenStepPropertiesProps {
  onAssignmentChange: (
    laneAssignment: boolean,
    assignment: IWFNodeAssignment,
    sendNotification?: boolean | undefined,
    addUserInteractionsToMail?: boolean | undefined,
    addGoToTaskButtonToMail?: boolean | undefined,
    mailInteractions?: IMailInteraction[] | undefined,
    userInteractions?: IUserInteraction[] | undefined,
    useCustomTemplate?: boolean | undefined,
    templateId?: string,
    templateParameters?: ITemplateParameter[] | undefined
  ) => void;
}

class WorkflowScreenStepProperties extends PureSdComponent<
  IWorkflowScreenStepPropertiesProps & IWithSDProps & IWithContextProp,
  IWorkflowScreenStepPropertiesState
> {
  private _miUnsub?: () => void;

  constructor(props: IWorkflowScreenStepPropertiesProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      step: {} as IWFStep,
      screen: undefined,
      inputPanel: true,
      loading: false,
      mailInteractionModalVisible: false,
      userInteractionModalVisible: false,
      projectList: [],
      visibleAppsDropdown: false,
      searchText: "",
      filteredProjectList: [],
      visibleMailInteractionDropdown: false,
      advancedPropertiesVisible: false
    };
  }

  componentDidMount(): void {
    this.setState({ step: this.props.sd.getSelectedWfStep()! });

    const projectsWithExternalWf: IProjectsWithExternalWFResponseDto[] =
      [
        ...this.props.sd.projectsWithExternalWFData,
        {
          id: this.props.sd.project.id,
          name: this.props.sd.project.name,
          processScreenInfo: [
            {
              projectId: this.props.sd.project.id,
              processId: this.props.sd.selectedWf?.id,
              stepId: this.props.sd.getSelectedWfStep()?.id
            }
          ]
        } as IProjectsWithExternalWFResponseDto
      ].filter(
        (p) =>
          p.processScreenInfo.length > 0 &&
          p.processScreenInfo.some(
            (s) =>
              s.projectId === this.props.sd.project.id &&
              s.processId === this.props.sd.selectedWf?.id &&
              s.stepId === this.props.sd.getSelectedWfStep()?.id
          )
      ) ?? [];
    this.setState({ projectList: projectsWithExternalWf, filteredProjectList: projectsWithExternalWf });
    this.attachMiWatcher();
  }

  componentDidUpdate(prevState, prevProps): void {
    const prevId = prevProps?.sd?.getSelectedWfStep?.()?.id ?? prevProps?.sd?.selectedWfNode?.id;
    const curId = this.props.sd?.getSelectedWfStep?.()?.id ?? this.props.sd?.selectedWfNode?.id;
    if (prevId !== curId) {
      this.attachMiWatcher();
    }
    if (prevState.step?.id !== this.state.step?.id) this.setState({ step: this.props.sd.getSelectedWfStep()! });
    this.updateTemplateParameters();
  }

  componentWillUnmount(): void {
    this.detachMiWatcher();
  }

  /** The screenId mapped to this step, or undefined when none is set. */
  getMappedScreenId = (): string | undefined => {
    const { step } = this.state;
    const project = this.props.sd.project;
    const id = project.processStepScreenIds?.find(
      (c) => c.projectId === project.id && c.processId === this.props.sd.selectedWf?.id && c.stepId === step.id
    )?.screenId;
    return id && id !== "" ? id : undefined;
  };

  /** The section-title info glyph; wraps in a hover tooltip when hint copy is supplied. */
  renderSectionInfo = (tooltip?: string) => {
    const icon = <KuikaSvg name="info-circle" width={13} height={13} color="var(--dark-5)" />;
    if (!tooltip) return icon;
    return <KuikaTooltip content={tooltip}>{icon}</KuikaTooltip>;
  };

  /**
   * Screen section body: the "set up" affordance when no screen is mapped, or the
   * screen name + "open" link when one is. Mirrors the design's empty↔set states.
   */
  renderScreenRow = () => {
    const screenId = this.getMappedScreenId();
    if (!screenId) {
      return (
        <div className="kuika-wf-step__screen kuika-wf-step__screen--empty">
          <KuikaSvg name="device-desktop" width={14} height={14} color="var(--dark-5)" />
          <span className="kuika-wf-step__screen-text">No screen set for step yet</span>
          <button type="button" className="kuika-wf-step__link" onClick={this.onClickWorkflowScreensButton}>
            SET UP
            <KuikaSvg name="arrow-right" type="arrows" width={13} height={13} color="var(--blue-5)" />
          </button>
        </div>
      );
    }
    const screenName = this.props.sd.screenList.find((s) => s.id === screenId)?.name;
    return (
      <div className="kuika-wf-step__screen">
        <KuikaSvg name="device-desktop" width={14} height={14} color="var(--dark-6)" />
        <span className="kuika-wf-step__screen-text">
          Screen: <strong>{screenName ?? "Screen no longer exists"}</strong>
        </span>
        {screenName && (
          <button type="button" className="kuika-wf-step__link" onClick={(e) => this.goToScreen(e, screenId)}>
            OPEN
            <KuikaSvg name="external-link" width={13} height={13} color="var(--blue-5)" />
          </button>
        )}
      </div>
    );
  };

  onUpdateSelectedParamsToMap = (selectedParams) => {
    const selectedStep: IWFStep | undefined = this.props.sd.getSelectedWfStep();
    this.props.sd.setWFStepParameters(selectedStep!, selectedParams, EWFParamType.Output);

    // user Interactions
    let userInteractions = selectedStep?.userInteractions ?? [];

    // remove parameters from user interactions
    userInteractions.forEach((mi) => {
      mi.parameters.forEach((p) => {
        if (selectedParams.find((sp) => sp.id === p.id) === undefined) {
          mi.parameters = mi.parameters.filter((mp) => mp.id !== p.id);
        }
      });
    });

    this.handleUserInteractions(userInteractions);
  };

  getStepAssignment = () => {
    if (!this.state.step.assignment) {
      this.state.step.assignment = {
        assignmentType: EWFAssignmentType.everyone,
        assignmentId: "Everyone",
        roleAssignmentParameter: undefined,
        dynamicAssignmentAction: undefined
      };
    }
    if (this.state.step.assignment && this.state.step.assignment.assignmentType === EWFAssignmentType.role) {
      return this.state.step.assignment.roleAssignmentParameter?.name;
    }
    return this.props.sd.getAssignmentTypeValue(this.state.step.assignment.assignmentType);
  };

  goToScreen = (e, selectedScreenId) => {
    e.preventDefault();
    this.props.sd.setDashboardState(DashboardState.design);
    this.props.sd.setActiveLeftPanelByClick(LeftPanelType.Screens);
    this.props.sd.loadScreen(selectedScreenId);
  };

  toggleLaneAssignment = async (val: boolean): Promise<void> => {
    this.setState({ loading: true });
    await this.props.onAssignmentChange(val, this.state.step.assignment);
    this.setState({ loading: false });
  };

  /** Leading glyph for a section head, before the title. */
  renderSectionIcon = (name: string): JSX.Element => (
    <KuikaSvg name={name} width={14} height={14} color="var(--dark-9)" className="kuika-wf-step__section-icon" />
  );

  handleStepAssignmentChange = async (assignment) => {
    const { step } = this.state;
    const mi = getBpmnMiState(step.id);
    if (mi.enabled) {
      const currentSource = readMiCollectionSource(step);
      if (currentSource === "Assignment") {
        const isCompatible =
          assignment.assignmentType === EWFAssignmentType.role ||
          assignment.assignmentType === EWFAssignmentType.dynamic;

        if (!isCompatible) {
          await writeMiCollectionSource(this.props.sd, step, "Manual", ESdChangeReason.WFStepMappingChange);
        }
      }
    }
    this.props.onAssignmentChange(step.useLaneAssignment, assignment);
  };

  toggleUserNotification = (val) => {
    const { step } = this.state;
    step.notifyUser = val;
    if (step.notifyUser === false) {
      step.addUserInteractionsToMail = false;
      step.mailInteractions = [];
      step.addGoToTaskButtonToMail = false;
      step.selectedAppIdsForNotification = [];
      step.templateId = undefined;
      step.templateParameters = undefined;
      step.useCustomTemplate = false;
    }
    this.props.onAssignmentChange(
      step.useLaneAssignment,
      step.assignment,
      val,
      step.addUserInteractionsToMail,
      step.addGoToTaskButtonToMail,
      step.mailInteractions,
      step.userInteractions,
      step.useCustomTemplate,
      step.templateId,
      step.templateParameters
    );
    this.setState({ step });
  };

  updateStepDescription = (desc) => {
    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;
    const selectedStep: IWFStep | undefined = this.props.sd.getSelectedWfStep();
    if (wf && selectedStep) {
      selectedStep.description = desc;
      this.props.sd.saveWorkFlow(wf);
    }
  };

  toggleAddUserInteractionsToNotification = (val) => {
    const { step } = this.state;
    step.addUserInteractionsToMail = val;
    this.props.onAssignmentChange(step.useLaneAssignment, step.assignment, step.notifyUser, val);
    this.setState({ step });
  };

  toggleAddGoToTaskToNotification = (val) => {
    const { step } = this.state;
    step.addGoToTaskButtonToMail = val;
    this.props.onAssignmentChange(
      step.useLaneAssignment,
      step.assignment,
      step.notifyUser,
      step.addUserInteractionsToMail,
      val
    );
    this.setState({ step });
  };

  handleMailInteractions = (val: IMailInteraction[]) => {
    const { step } = this.state;
    step.mailInteractions = val;
    this.props.onAssignmentChange(
      step.useLaneAssignment,
      step.assignment,
      step.notifyUser,
      step.addUserInteractionsToMail,
      step.addGoToTaskButtonToMail,
      val
    );
    this.setState({ step });
  };

  /** Jump to the Forms → Interactions tab for this step (interaction editing lives there now). */
  goToInteractions = (stepId?: string): void => {
    requestProcessEditorTab(PROCESS_TAB_FORMS);
    if (stepId) requestFormsInteractions(stepId);
  };

  handleUserInteractions = (val: IUserInteraction[]) => {
    const { step } = this.state;
    step.userInteractions = val;
    this.props.onAssignmentChange(
      step.useLaneAssignment,
      step.assignment,
      step.notifyUser,
      step.addUserInteractionsToMail,
      step.addGoToTaskButtonToMail,
      step.mailInteractions,
      val
    );
    this.setState({ step });
  };

  DueChangeHandler = async (value, field) => {
    this.setState({ loading: true });
    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;
    const selectedStep: IWFStep | undefined = this.props.sd.getSelectedWfStep();
    if (wf && selectedStep && selectedStep) {
      if (!selectedStep.dueIn)
        selectedStep.dueIn = {
          dueUnit: EWFDueUnit.Days,
          dueValue: 1
        };

      if (field === "unit") {
        selectedStep.dueIn.dueUnit = value as EWFDueUnit;
      } else if (field === "value") {
        selectedStep.dueIn.dueValue = value;
      }
      await this.props.sd.saveWorkFlow(wf);
    }
    this.setState({ loading: false });
  };

  updateAppsInSelections = (appId: string, checked: boolean) => {
    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;
    const selectedStep: IWFStep | undefined = this.props.sd.getSelectedWfStep();
    if (wf && selectedStep) {
      if (!selectedStep.selectedAppIdsForNotification) {
        selectedStep.selectedAppIdsForNotification = [];
      }
      if (checked && !selectedStep.selectedAppIdsForNotification?.includes(appId)) {
        selectedStep.selectedAppIdsForNotification?.push(appId);
      } else {
        selectedStep.selectedAppIdsForNotification = selectedStep.selectedAppIdsForNotification?.filter(
          (id) => id !== appId
        );
      }
      this.props.sd.saveWorkFlow(wf);
    }
  };

  updateMailInteractionInSelections = (userInteractionId: string, checked: boolean) => {
    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;
    const selectedStep: IWFStep | undefined = this.props.sd.getSelectedWfStep();
    if (wf && selectedStep) {
      if (!selectedStep.mailInteractions) {
        selectedStep.mailInteractions = [];
      }
      if (checked && !selectedStep.mailInteractions?.map((mi) => mi.userInteractionId).includes(userInteractionId)) {
        selectedStep.mailInteractions?.push({ userInteractionId } as IMailInteraction);
      } else {
        selectedStep.mailInteractions = selectedStep.mailInteractions?.filter(
          (mi) => mi.userInteractionId !== userInteractionId
        );
      }
      selectedStep.mailInteractions = selectedStep.mailInteractions.filter(
        (mi) =>
          mi !== undefined &&
          mi !== null &&
          mi.userInteractionId !== "" &&
          mi.userInteractionId !== undefined &&
          mi.userInteractionId !== null &&
          selectedStep.userInteractions?.map((ui) => ui.id).includes(mi.userInteractionId)
      );
      this.props.sd.saveWorkFlow(wf);
    }
  };

  handleAppSearchInput = (e) => {
    const value = e.target.value.toLowerCase().trim();
    const filtered = this.state.projectList.filter((item) => item.name.toLowerCase().includes(value));

    if (value === "") {
      this.setState({ filteredProjectList: this.state.projectList, searchText: value });
      return;
    }
    this.setState({ filteredProjectList: filtered, searchText: value });
  };

  /**
   * "SET UP" on a screenless step: step-screen setup lives on the Forms tab now (the legacy
   * step-screen wizard modal is retired for this path) — jump there with the step pre-selected on
   * the Workflow Screens sub-tab, mirroring goToInteractions.
   */
  onClickWorkflowScreensButton = (): void => {
    requestProcessEditorTab(PROCESS_TAB_FORMS);
    requestFormsStep(this.state.step.id);
  };

  updateTemplateParameters = () => {
    const { step } = this.state;
    if (!step.templateId) return;

    const template = this.props.sd.notificationTemplates?.find((t) => t.id === step.templateId);
    if (template?.parameters) {
      const parameters: string[] = JSON.parse(template.parameters).Parameter as string[];

      const uniqueParameters = parameters.filter((p, i, a) => a.indexOf(p) === i);

      if (
        step.templateParameters?.filter((p) => p.name !== "subject")?.length === uniqueParameters.length &&
        step.templateParameters?.filter((p) => p.name !== "subject").every((p) => uniqueParameters.includes(p.name))
      ) {
        return;
      }

      uniqueParameters.forEach((p) => {
        const param = step.templateParameters?.find((tp) => tp.name === p);
        if (!param) {
          step.templateParameters!.push({
            name: p,
            value: { category: ESymbolCategory.fixed, fixedValue: "" } as ISymbol
          });
        }
      });
    }

    const parameters: string[] =
      (JSON.parse(template?.parameters && template?.parameters !== "" ? template?.parameters : "[]")
        .Parameter as string[]) ?? [];

    const uniqueParameters = parameters.filter((p, i, a) => a.indexOf(p) === i);

    step.templateParameters =
      step.templateParameters
        ?.filter((tp) => tp.name === "subject" || uniqueParameters.includes(tp.name))
        .filter((tp, index, arr) => arr.findIndex((x) => x.name === tp.name) === index) ?? step.templateParameters;

    this.props.onAssignmentChange(
      step.useLaneAssignment,
      step.assignment,
      step.notifyUser,
      step.addUserInteractionsToMail,
      step.addGoToTaskButtonToMail,
      step.mailInteractions,
      step.userInteractions,
      step.useCustomTemplate,
      step.templateId,
      step.templateParameters
    );

    this.setState({ step });
  };

  handleTemplateId = (val: string) => {
    const { step } = this.state;

    if (step.templateId !== val) {
      step.templateParameters = [
        {
          name: "subject",
          value: { category: ESymbolCategory.fixed, fixedValue: "" } as ISymbol
        }
      ];
      const template = this.props.sd.notificationTemplates?.find((t) => t.id === val);
      if (template?.parameters) {
        const parameters: string[] = JSON.parse(template.parameters).Parameter as string[];
        const uniqueParameters = parameters.filter((p, i, a) => a.indexOf(p) === i);

        uniqueParameters.forEach((p) => {
          step.templateParameters!.push({
            name: p,
            value: { category: ESymbolCategory.fixed, fixedValue: "" } as ISymbol
          });
        });
      }
    }

    step.templateId = val;
    this.props.onAssignmentChange(
      step.useLaneAssignment,
      step.assignment,
      step.notifyUser,
      step.addUserInteractionsToMail,
      step.addGoToTaskButtonToMail,
      step.mailInteractions,
      step.userInteractions,
      step.useCustomTemplate,
      val,
      step.templateParameters
    );

    this.setState({ step });
  };

  handleUseCustomTemplate = (val: boolean) => {
    const { step } = this.state;
    step.useCustomTemplate = val;
    this.props.onAssignmentChange(
      step.useLaneAssignment,
      step.assignment,
      step.notifyUser,
      step.addUserInteractionsToMail,
      step.addGoToTaskButtonToMail,
      step.mailInteractions,
      step.userInteractions,
      val,
      step.templateId,
      step.templateParameters
    );
    this.setState({ step });
  };

  handleTemplateParamChange = (symbol: ISymbol, paramName: string) => {
    const { step } = this.state;
    const param = step.templateParameters?.find((p) => p.name === paramName);
    if (param) {
      param.value = symbol;
    }

    this.props.onAssignmentChange(
      step.useLaneAssignment,
      step.assignment,
      step.notifyUser,
      step.addUserInteractionsToMail,
      step.addGoToTaskButtonToMail,
      step.mailInteractions,
      step.userInteractions,
      step.useCustomTemplate,
      step.templateId,
      step.templateParameters
    );

    this.setState({ step });
  };

  renderAdvancedPropertiesTab = () => {
    const selectedTab = this.props.sd.getActiveRightPanel();
    if (selectedTab !== RightPanelType.AdvancedProperties) {
      this.setState({ advancedPropertiesVisible: false });
    }
    return (
      <div className="kuika-flex-center kuika-padding-left-m kuika-padding-right-m kuika-padding-top-s ">
        <KuikaTabs
          options={["Properties", "Advanced"]}
          value={this.state.advancedPropertiesVisible === false ? "Properties" : "Advanced"}
          onChange={(value) => {
            if (value === "Properties") {
              this.props.sd.setActiveRightPanelByClick(RightPanelType.Properties);
              this.setState({ advancedPropertiesVisible: false });
            } else if (value === "Advanced") {
              this.props.sd.setActiveRightPanelByClick(RightPanelType.AdvancedProperties);
              this.setState({ advancedPropertiesVisible: true });
            }
          }}
        />
      </div>
    );
  };

  resolveSelectedStep = (): IWFStep | undefined => {
    const bySel = this.props.sd.getSelectedWfStep?.();
    if (bySel?.id) return bySel;
    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;
    const nid = this.props.sd?.selectedWfNode?.id;
    const fromNodes = nid ? wf?.nodes?.steps?.find((s) => s.id === nid) : undefined;
    return fromNodes;
  };

  renderMultiInstanceSection = () => {
    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;
    const step = this.resolveSelectedStep();
    if (!wf || !step?.id) return null;

    const mi = getBpmnMiState(step.id);
    if (!mi.enabled) return null;

    // Each child renders its own `.kuika-wf-step__section` blocks (Instance Generation,
    // Cancel Condition, Aggregation), so no umbrella header or wrapper section here.
    return (
      <>
        {mi.mode === "PMI" && <PmiDecisionProperties step={step} />}
        {mi.mode === "SMI" && <SmiDecisionProperties step={step} />}
        <AggregationProperties step={step} />
      </>
    );
  };

  getSelectedStepId = (): string | undefined => {
    const step = this.props.sd.getSelectedWfStep?.();
    return step?.id ?? this.props.sd?.selectedWfNode?.id;
  };

  attachMiWatcher = () => {
    this.detachMiWatcher();
    const stepId = this.getSelectedStepId();
    if (!stepId) return;
    this._miUnsub = watchBpmnMi(stepId, () => {
      this.setState({ miStamp: Date.now() });
    });
  };

  detachMiWatcher = () => {
    try {
      this._miUnsub && this._miUnsub();
    } finally {
      this._miUnsub = undefined;
    }
  };

  renderLoopSection = () => {
    const step = this.resolveSelectedStep?.() || this.props.sd.getSelectedWfStep?.();
    if (!step?.id) return null;
    return <LoopDecisionProperties step={step} />;
  };

  render() {
    const { screen, step } = this.state;
    let wfParameters: IWFParameter[] = [];
    const selectedStep: IWFStep | undefined = this.props.sd.getSelectedWfStep();
    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;

    if (wf && wf.parameters) {
      wfParameters = wf.parameters ? wf.parameters : [];
    }

    // Both notification pickers share the same checkbox-list dropdown as the Step Outputs
    // mapper (KuikaDropdownMenu + KuikaCheckbox rows behind a search header), so multi-select
    // reads the same everywhere in this panel.
    const dropdownSearchHeader: JSX.Element = (
      <KuikaInput
        placeholder="Search..."
        value={this.state.searchText}
        onChange={this.handleAppSearchInput}
        size="md"
        autoFocus
      />
    );

    const appOptions: KuikaDropdownMenuItemType[] = this.state.filteredProjectList.map((p) => {
      const isSelected: boolean = selectedStep?.selectedAppIdsForNotification?.includes(p.id) ?? false;
      return {
        key: p.id,
        value: p.id,
        label: (
          <KuikaCheckbox
            checked={isSelected}
            onChange={(e) => this.updateAppsInSelections(p.id, e.target.checked)}
            label={p.name}
          />
        ),
        onClick: () => this.updateAppsInSelections(p.id, !isSelected),
        showSelectedIcon: false
      };
    });

    const mailInteractionOptions: KuikaDropdownMenuItemType[] = (selectedStep?.userInteractions ?? [])
      .filter(
        (p) => this.state.searchText === "" || p.label.toLowerCase().includes(this.state.searchText.toLowerCase())
      )
      .map((p) => {
        const isSelected: boolean =
          selectedStep?.mailInteractions?.map((mi) => mi.userInteractionId).includes(p.id) ?? false;
        // A notification completes the step with NO form, so an interaction whose Business Object
        // outputs carry no value can't be offered — triggering it from a mail would write the BO
        // back empty. A stale selection (made before this rule, or whose value was cleared since)
        // stays actionable so the user can still UNcheck it — but can't be re-checked once off.
        // The reason renders as an always-visible hint line, not a hover tooltip.
        const notifiable: boolean = isInteractionNotifiable(this.props.sd.selectedWf, selectedStep, p);
        const blocked: boolean = !notifiable && !isSelected;
        return {
          key: p.id,
          value: p.id,
          label: (
            <span className="kuika-wf-step__mail-interaction">
              <KuikaCheckbox
                checked={isSelected}
                disabled={blocked}
                onChange={(e) => this.updateMailInteractionInSelections(p.id, e.target.checked)}
                label={p.label}
              />
              {!notifiable && (
                <span className="kuika-wf-step__mail-interaction-hint">
                  Needs Business Object values — set them in Forms &gt; Interactions
                </span>
              )}
            </span>
          ),
          onClick: () => {
            if (!blocked) this.updateMailInteractionInSelections(p.id, !isSelected);
          },
          showSelectedIcon: false
        };
      });

    return (
      <>
        {this.renderAdvancedPropertiesTab()}
        <KuikaTabContent
          activeKey={this.state.advancedPropertiesVisible ? ADVANCED_STEP_TAB : PROPERTIES_STEP_TAB}
          order={STEP_TAB_ORDER}
          render={(tab: string): ReactNode =>
            tab === ADVANCED_STEP_TAB ? (
              <div className="kuika-wf-step">
                <section className="kuika-wf-step__section">
                  <div className="kuika-wf-step__section-head">
                    {this.renderSectionIcon("clock")}
                    <span className="kuika-wf-step__section-title" data-testid="wfStepSection-due">
                      Due
                    </span>
                    {this.renderSectionInfo("How long the task may stay open before it is considered overdue")}
                  </div>
                  <WorkflowDue
                    id="task-due"
                    due={selectedStep!.dueIn}
                    onDueUnitChange={(val) => this.DueChangeHandler(val, "unit")}
                    onDueValueChange={(val) => this.DueChangeHandler(val, "value")}
                    loading={this.state.loading}
                  />
                </section>

                <section className="kuika-wf-step__section">
                  <div className="kuika-wf-step__section-head">
                    {this.renderSectionIcon("bolt")}
                    <span className="kuika-wf-step__section-title" data-testid="wfStepSection-pre-actions">
                      Pre Actions
                    </span>
                    {this.renderSectionInfo()}
                  </div>
                  <p className="kuika-wf-step__hint">Add your custom actions to run on step creation</p>
                  <ActionPicker hideHeader />
                </section>

                <section className="kuika-wf-step__section">
                  <div className="kuika-wf-step__section-head">
                    {this.renderSectionIcon("bell")}
                    <span className="kuika-wf-step__section-title" data-testid="wfStepSection-notification">
                      Notification
                    </span>
                    {this.renderSectionInfo()}
                  </div>

                  <div className="kuika-wf-step__field-row">
                    <span className="kuika-wf-step__field-label">Send</span>
                    <span className="kuika-wf-step__field-control">
                      <KuikaTabs
                        options={[
                          { value: "false", icon: <KuikaSvg name="x" width={12} height={12} /> },
                          { value: "true", icon: <KuikaSvg name="check" width={12} height={12} /> }
                        ]}
                        onChange={(val) => {
                          val === "true" ? this.toggleUserNotification(true) : this.toggleUserNotification(false);
                        }}
                        value={step.notifyUser ? "true" : "false"}
                      />
                    </span>
                  </div>

                  {step.notifyUser === true && (
                    <div className="kuika-wf-step__field-row">
                      <span className="kuika-wf-step__field-label">Template</span>
                      <span className="kuika-wf-step__field-control">
                        <KuikaTabs
                          options={["Default", "Custom"]}
                          disabled={selectedStep?.assignment?.assignmentType === EWFAssignmentType.external}
                          onChange={(val) => {
                            val === "Custom" ? this.handleUseCustomTemplate(true) : this.handleUseCustomTemplate(false);
                          }}
                          value={step.useCustomTemplate ? "Custom" : "Default"}
                        />
                      </span>
                    </div>
                  )}

                  {step.notifyUser === true && (
                    <div className="kuika-wf-step__card">
                      <KuikaTabContent
                        activeKey={step.useCustomTemplate ? CUSTOM_MAIL_TEMPLATE : DEFAULT_MAIL_TEMPLATE}
                        order={MAIL_TEMPLATE_ORDER}
                        render={(templateTab: string): ReactNode =>
                          templateTab === CUSTOM_MAIL_TEMPLATE ? (
                            <div>
                              <span className="kuika-wf-step__form-label">Template</span>
                              <KuikaSelectbox
                                value={selectedStep?.templateId}
                                onChange={(val: string) => this.handleTemplateId(val)}
                                className="kuika-wf-step__select"
                                size="sm"
                                searchable
                                getPopupContainer={(trigger: HTMLElement): HTMLElement =>
                                  trigger.parentNode as HTMLElement
                                }
                                options={(this.props.sd.notificationTemplates ?? [])
                                  .filter((template) => template.templateType === ENotificationTemplateType.Email)
                                  .sort((a, b) => a.name.localeCompare(b.name))
                                  .map((template): KuikaSelectboxOption => ({
                                    key: template.id,
                                    value: template.id,
                                    label: template.name
                                  }))}
                              />

                              {step.templateParameters &&
                                step.templateParameters.map((parameter) => {
                                  return (
                                    <div key={parameter.name}>
                                      <div className="kuika-wf-step__template-param">
                                        <span className="kuika-wf-step__template-param-name">
                                          <ActionParameterTitle
                                            parameter={{
                                              name: parameter.name.replaceAll("@", ""),
                                              Typename: "String",
                                              cardinality: ECardinality.One
                                            }}
                                          />
                                        </span>
                                        <SymbolPicker
                                          symbol={parameter.value}
                                          changeType="onBlur"
                                          valueType={"String"}
                                          targetCardinality={ECardinality.One}
                                          onSelectedSymbol={(
                                            symbol: ISymbol,
                                            changeReason?: ESdChangeReason | undefined
                                          ) => {
                                            this.handleTemplateParamChange(symbol, parameter.name);
                                          }}
                                        />
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          ) : (
                            <>
                              <div className="kuika-wf-step__switch-row">
                                <span className="kuika-wf-step__switch-label">Add "Go To Task" Button to Mail</span>
                                <KuikaSwitch
                                  labelPosition="none"
                                  disabled={
                                    selectedStep?.assignment?.assignmentType === EWFAssignmentType.external ||
                                    this.state.loading
                                  }
                                  onChange={(checked: boolean) => this.toggleAddGoToTaskToNotification(checked)}
                                  checked={step.addGoToTaskButtonToMail}
                                />
                              </div>

                              {step.addGoToTaskButtonToMail === true && (
                                <KuikaDropdownMenu
                                  options={appOptions}
                                  open={this.state.visibleAppsDropdown}
                                  setOpen={(open: boolean) => this.setState({ visibleAppsDropdown: open })}
                                  header={dropdownSearchHeader}
                                  overlayClassName="kuika-select-parameters-dropdown"
                                  placement="bottomLeft"
                                  trigger="click"
                                  closeOnSelect={false}
                                >
                                  {() => (
                                    <KuikaButton
                                      type="secondary"
                                      size="md"
                                      className="kuika-wf-step__picker-trigger"
                                      onClick={() =>
                                        this.setState({ visibleAppsDropdown: !this.state.visibleAppsDropdown })
                                      }
                                    >
                                      Select Apps
                                      <KuikaSvg type="arrows" name="chevron-down" width={14} height={14} pointer />
                                    </KuikaButton>
                                  )}
                                </KuikaDropdownMenu>
                              )}

                              <div className="kuika-wf-step__switch-row">
                                <span className="kuika-wf-step__switch-label">Add "User Interactions" to Mail</span>
                                <KuikaSwitch
                                  labelPosition="none"
                                  disabled={
                                    selectedStep?.assignment?.assignmentType === EWFAssignmentType.external ||
                                    this.state.loading
                                  }
                                  onChange={(checked: boolean) => this.toggleAddUserInteractionsToNotification(checked)}
                                  checked={step.addUserInteractionsToMail}
                                />
                              </div>

                              {step.addUserInteractionsToMail === true && (
                                <KuikaDropdownMenu
                                  options={mailInteractionOptions}
                                  open={this.state.visibleMailInteractionDropdown}
                                  setOpen={(open: boolean) => this.setState({ visibleMailInteractionDropdown: open })}
                                  header={dropdownSearchHeader}
                                  overlayClassName="kuika-select-parameters-dropdown kuika-mail-interactions-dropdown"
                                  placement="bottomLeft"
                                  trigger="click"
                                  closeOnSelect={false}
                                >
                                  {() => (
                                    <KuikaButton
                                      type="secondary"
                                      size="md"
                                      className="kuika-wf-step__picker-trigger"
                                      onClick={() =>
                                        this.setState({
                                          visibleMailInteractionDropdown: !this.state.visibleMailInteractionDropdown
                                        })
                                      }
                                    >
                                      Select Mail Interactions
                                      <KuikaSvg type="arrows" name="chevron-down" width={14} height={14} pointer />
                                    </KuikaButton>
                                  )}
                                </KuikaDropdownMenu>
                              )}
                            </>
                          )
                        }
                      />
                    </div>
                  )}
                </section>
                {this.renderMultiInstanceSection()}
                {this.renderLoopSection()}
              </div>
            ) : (
              <div className="kuika-wf-step">
                <section className="kuika-wf-step__section">
                  <div className="kuika-wf-step__section-head">
                    {this.renderSectionIcon("device-desktop")}
                    <span className="kuika-wf-step__section-title" data-testid="wfStepSection-screen">
                      Screen
                    </span>
                    {this.renderSectionInfo()}
                  </div>
                  {this.renderScreenRow()}
                </section>

                <section className="kuika-wf-step__section">
                  <div className="kuika-wf-step__section-head">
                    {this.renderSectionIcon("file-text")}
                    <span className="kuika-wf-step__section-title" data-testid="wfStepSection-description">
                      Description
                    </span>
                    {this.renderSectionInfo()}
                  </div>
                  <WorkflowStepDescription
                    wf={wf!}
                    step={selectedStep!}
                    handleDescriptionChange={(val) => this.updateStepDescription(val)}
                    key={selectedStep?.id}
                  ></WorkflowStepDescription>
                </section>

                <section className="kuika-wf-step__section">
                  <div className="kuika-wf-step__section-head">
                    {this.renderSectionIcon("user")}
                    <span className="kuika-wf-step__section-title" data-testid="wfStepSection-assignment">
                      Assignment
                    </span>
                    {this.renderSectionInfo()}
                  </div>
                  <WorkflowAssignmentProperties
                    wf={wf!}
                    nodeAssignment={selectedStep?.assignment!}
                    onNodeAssignmentDataChanged={async (val) => this.handleStepAssignmentChange(val)}
                    disabled={selectedStep?.useLaneAssignment !== undefined ? selectedStep?.useLaneAssignment : true}
                    useLaneAssignment={step.useLaneAssignment ?? false}
                    onUseLaneChange={(val: boolean) => this.toggleLaneAssignment(val)}
                    hideLabel
                  ></WorkflowAssignmentProperties>
                </section>

                <section className="kuika-wf-step__section">
                  <div className="kuika-wf-step__section-head">
                    {this.renderSectionIcon("arrows-horizontal")}
                    <span className="kuika-wf-step__section-title" data-testid="wfStepSection-step-outputs">
                      Step Outputs
                    </span>
                    {this.renderSectionInfo(
                      'You will use "Resume Process" action to set output mappings on selected parameters'
                    )}
                    <WorkflowStepOutputMapping
                      wf={wf!}
                      step={selectedStep!}
                      onUpdateSelectedParamsToMap={(selectedParams) => this.onUpdateSelectedParamsToMap(selectedParams)}
                      key={selectedStep!.id}
                      triggerIcon
                      triggerClassName="kuika-wf-step__add"
                    />
                  </div>
                  {selectedStep?.outputParameters && selectedStep.outputParameters.length > 0 ? (
                    <div className="kuika-wf-step__param-list">
                      {selectedStep.outputParameters.map((p) => {
                        const wfPrm = wf?.parameters?.find((wp) => wp.id === p.id);
                        if (wfPrm === undefined) return <></>;
                        return (
                          <div key={wfPrm.id} className="kuika-wf-step__param">
                            <span className="kuika-wf-step__param-icon">
                              {this.props.sd.getTypeIcon(
                                wfPrm.isObject && wfPrm.dataTypeName ? wfPrm.dataTypeName : wfPrm.dataType.toString()
                              )}
                            </span>
                            <span className="kuika-wf-step__param-name">{wfPrm.name}</span>
                            <span className="kuika-wf-step__param-type">({getWorkflowParamTypeLabel(wfPrm)})</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <ActionSectionEmptyState
                      message="No outputs mapped yet."
                      hint="Pick the parameters this step writes back."
                      flush
                    />
                  )}
                </section>

                <section className="kuika-wf-step__section">
                  <div className="kuika-wf-step__section-head">
                    {this.renderSectionIcon("click")}
                    <span className="kuika-wf-step__section-title" data-testid="wfStepSection-user-interactions">
                      User Interactions
                    </span>
                  </div>

                  {selectedStep?.userInteractions && selectedStep.userInteractions.length > 0 ? (
                    <div className="kuika-wf-step__interaction-list">
                      {selectedStep.userInteractions.map((interaction) => (
                        <span key={interaction.id} className="kuika-wf-step__interaction">
                          {interaction.label}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <ActionSectionEmptyState
                      message="No interactions yet."
                      hint="Manage them in Forms → Interactions."
                      flush
                    />
                  )}

                  <KuikaButton
                    type="text"
                    size="md"
                    onClick={() => this.goToInteractions(selectedStep?.id)}
                    data-testid="workflowGoToInteractions"
                  >
                    Manage in Interactions tab
                    <KuikaSvg name="external-link" width={12} height={12} />
                  </KuikaButton>
                </section>
              </div>
            )
          }
        />
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.SaveWorkflow ||
      changeReason == ESdChangeReason.WFStepMappingChange ||
      changeReason === ESdChangeReason.ClickWfDesignXmlNode
    ) {
      return true;
    }

    return false;
  };
}

const workflowScreenStepProperties = withContext<IWorkflowScreenStepPropertiesProps>(
  withSD<IWorkflowScreenStepPropertiesProps>(WorkflowScreenStepProperties)
);
export { workflowScreenStepProperties as WorkflowScreenStepProperties };
